import React, { useState, useEffect } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  isSameMonth,
  isSameDay,
  isToday
} from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { cn } from '@/lib/utils'
import type { CalendarEvent } from '@/hooks/useCalendarEvents'

interface CalendarViewProps {
  events: CalendarEvent[]
  selectedDate: Date
  onDateSelect: (date: Date) => void
  onMonthChange: (date: Date) => void
  loading?: boolean
}

const CalendarView: React.FC<CalendarViewProps> = ({
  events,
  selectedDate,
  onDateSelect,
  onMonthChange,
  loading = false
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date())

  useEffect(() => {
    onMonthChange(currentMonth)
  }, [currentMonth, onMonthChange])

  const handlePrevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1))
  }

  const handleNextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1))
  }

  const getEventsForDate = (date: Date): CalendarEvent[] => {
    const dateStr = format(date, 'yyyy-MM-dd')
    return events.filter(event => {
      const eventDate = event.start.split('T')[0]
      return eventDate === dateStr
    })
  }

  const renderHeader = () => {
    return (
      <div className="flex items-center justify-between mb-5 px-1">
        <button
          onClick={handlePrevMonth}
          className="p-2 border border-border rounded-xl bg-white hover:bg-[#F1F5F9] transition-colors duration-200 dark:bg-transparent dark:border-neutral-700 dark:hover:bg-neutral-800"
        >
          <ChevronLeft className="w-5 h-5 text-muted-foreground dark:text-neutral-400" />
        </button>
        <h2 className="text-lg font-semibold text-[#0F172A] capitalize dark:text-neutral-50">
          {format(currentMonth, 'MMMM yyyy', { locale: ptBR })}
        </h2>
        <button
          onClick={handleNextMonth}
          className="p-2 border border-border rounded-xl bg-white hover:bg-[#F1F5F9] transition-colors duration-200 dark:bg-transparent dark:border-neutral-700 dark:hover:bg-neutral-800"
        >
          <ChevronRight className="w-5 h-5 text-muted-foreground dark:text-neutral-400" />
        </button>
      </div>
    )
  }

  const renderDaysOfWeek = () => {
    const days = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab']
    return (
      <div className="grid grid-cols-7 mb-2">
        {days.map(day => (
          <div
            key={day}
            className="text-center text-xs font-medium text-[#64748B] py-2 dark:text-neutral-500"
          >
            {day}
          </div>
        ))}
      </div>
    )
  }

  const renderCells = () => {
    const monthStart = startOfMonth(currentMonth)
    const monthEnd = endOfMonth(monthStart)
    const startDate = startOfWeek(monthStart)
    const endDate = endOfWeek(monthEnd)

    const rows: React.ReactNode[] = []
    let days: React.ReactNode[] = []
    let day = startDate

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const cloneDay = day
        const dayEvents = getEventsForDate(day)
        const hasEvents = dayEvents.length > 0
        const isCurrentMonth = isSameMonth(day, currentMonth)
        const isSelected = isSameDay(day, selectedDate)
        const isTodayDate = isToday(day)

        days.push(
          <button
            key={day.toString()}
            onClick={() => onDateSelect(cloneDay)}
            className={cn(
              'relative aspect-square p-1 flex flex-col items-center justify-start rounded-xl transition-colors duration-200',
              !isCurrentMonth && 'opacity-30',
              isSelected && 'bg-[#EEF4FF] border border-[#BFDBFE] dark:bg-blue-500/20 dark:border-blue-500/50',
              !isSelected && 'hover:bg-[#F1F5F9] dark:hover:bg-neutral-800',
              isTodayDate && !isSelected && 'bg-[#EEF4FF]/60 border border-[#BFDBFE]/70 dark:bg-neutral-800 dark:border-blue-500/30'
            )}
          >
            <span
              className={cn(
                'text-sm font-medium',
                isSelected && 'text-[#2563EB] dark:text-[#60A5FA]',
                !isSelected && isTodayDate && 'text-[#2563EB] dark:text-[#60A5FA]',
                !isSelected && !isTodayDate && 'text-[#334155] dark:text-neutral-300'
              )}
            >
              {format(day, 'd')}
            </span>
            {hasEvents && (
              <div className="flex gap-0.5 mt-1">
                {dayEvents.slice(0, 3).map((_, idx) => (
                  <div
                    key={idx}
                    className="w-1.5 h-1.5 rounded-full bg-[#22C55E]"
                  />
                ))}
              </div>
            )}
          </button>
        )
        day = addDays(day, 1)
      }
      rows.push(
        <div key={day.toString()} className="grid grid-cols-7 gap-1">
          {days}
        </div>
      )
      days = []
    }

    return <div className="space-y-1">{rows}</div>
  }

  return (
    <div className="bg-white border border-border rounded-[20px] p-5 shadow-sm transition-colors duration-200 dark:bg-[#27272A] dark:border-[#3F3F464D] dark:shadow-none dark:rounded-xl">
      {renderHeader()}
      {renderDaysOfWeek()}
      {loading ? (
        <div className="h-64 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-[#2563EB] border-t-transparent rounded-full animate-spin dark:border-[#60A5FA]" />
        </div>
      ) : (
        renderCells()
      )}
    </div>
  )
}

export default CalendarView
